import { Lightning } from '@lightningjs/sdk'

import {
  black,
  getColor,
  grey100,
  grey300,
  grey50,
  grey800,
  grey920,
  primary600,
  transparent,
} from '../../constants/colors'
import { UniNeueBold, UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { Images } from '../../constants/paths'
import { TvCardStyle, TvCardStylesEnum } from '../../types/flo-enums'
import { CardItemModel, TvBadgeModel } from '../../types/flo-experience'
import { getAsset } from '../../utils/image'
import TvBadge from '../TvBadge/TvBadge'

export const INNER_PADDING = 16
export const INNER_MARGIN = 16
export const HIDDEN = 0.00001
export const DETAILS_FONT_SIZE = 28
export const TITLE_FONT_SIZE = 32

const BADGE_MARGIN = 8
const BADGE_CONTAINER_HEIGHT = 36
const LARGE_IMG_HEIGHT = 297
const SMALL_IMG_HEIGHT = 218
const FOOTER_FONT_SIZE = 28
const OVERLAY_WIDTH_ADJUSTMENT = 2
const OVERLAY_HEIGHT_ADJUSTMENT = 1

export const getCardTemplate = (
  style: TvCardStyle,
  cardWidth: number,
  data: CardItemModel,
  isVideo = false,
) => {
  if (style === TvCardStylesEnum.Large) {
    return getLargeTemplate(cardWidth, data, isVideo)
  }

  if (style === TvCardStylesEnum.Small) {
    return getSmallTemplate(cardWidth, data, isVideo)
  }

  return getMediumTemplate(cardWidth, data)
}

const toBadge = (badge: TvBadgeModel) => {
  return {
    type: TvBadge,
    data: badge,
    flexItem: {
      marginLeft: BADGE_MARGIN,
    },
  }
}

export class TvBadges extends Lightning.Component {
  static _template() {
    return {
      h: BADGE_CONTAINER_HEIGHT,
      flex: {
        direction: 'row',
      },
    }
  }

  set badges(v: TvBadgeModel[]) {
    const badges = v?.map((badge) => toBadge(badge))

    this.patch({
      children: badges,
      x: -(badges.length - 1 * (BADGE_MARGIN / 2)), // Offset to compesate for margin
    })
  }
}

export class TvCardImgPlaceholder extends Lightning.Component {
  static _template() {
    return {
      x: (w) => w / 2,
      y: (h) => h / 2,
      mount: 0.5,
      texture: null,
      alpha: 1,
    }
  }

  set imgSize(v: { w: number; h: number }) {
    this.patch({
      texture: Lightning.Tools.getSvgTexture(getAsset(Images.IMG_PLACEHOLDER), v.w, v.h),
    })
  }
}

const getProgressPercentage = (width: number, progress: number, duration: number) => {
  return (progress * width) / duration
}

const getVideoProgressAndDuration = (data: CardItemModel) => {
  const duration = data?.action?.content_metadata?.duration
  let progress = data?.action?.content_metadata?.progress_seconds

  return {
    duration,
    progress,
  }
}

export const getLargeTemplate = (cardWidth: number, data: CardItemModel, isVideo = false) => {
  const { duration, progress } = getVideoProgressAndDuration(data)

  return {
    flex: {
      direction: 'column',
    },
    collision: true,
    ImageWrapper: {
      w: (w) => w,
      h: LARGE_IMG_HEIGHT,
      rect: true,
      color: getColor(grey800),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: [BORDER_RADIUS, BORDER_RADIUS, 0, 0],
      },
      Placeholder: {
        type: TvCardImgPlaceholder,
        imgSize: { w: 220, h: 200 },
      },
      Image: {
        w: (w) => w,
        h: (h) => h,
        src: data?.preview_image?.url,
        alpha: HIDDEN,
      },
      TopBadges: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        TopLeadingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(data?.leading_top_badges?.length > 0 && {
            data: data.leading_top_badges[0],
          }),
          flexItem: {
            marginLeft: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
        TopTrailingBadges: {
          type: TvBadges,
          ...(data?.trailing_top_badges?.length > 0 && {
            badges: data.trailing_top_badges,
          }),
          flexItem: {
            marginRight: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
      },
      BottomBadges: {
        w: (w) => w,
        h: (h) => h,
        y: 229,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        BottomLeadingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(data?.leading_bottom_badges?.length > 0 && {
            data: data.leading_bottom_badges[0],
          }),
          flexItem: {
            marginLeft: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
        BottomTrailingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(isVideo &&
            duration && {
              duration,
            }),
          ...(data?.trailing_bottom_badges?.length > 0 && {
            badges: data.trailing_bottom_badges[0],
          }),
          flexItem: {
            marginRight: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
      },
      ...(isVideo &&
        duration &&
        progress > 0 && {
          ProgressBar: {
            w: (w) => w,
            h: 6,
            y: 291,
            rect: true,
            color: getColor(grey100),
            shader: {
              type: Lightning.shaders.RoundedRectangle,
              radius: 1,
            },
            Progress: {
              w: getProgressPercentage(cardWidth, progress, duration),
              h: (h) => h,
              rect: true,
              color: getColor(primary600),
              shader: {
                type: Lightning.shaders.RoundedRectangle,
                radius: [0, 1, 1, 0],
              },
            },
          },
        }),
    },
    MetadataContainer: {
      w: (w) => w,
      h: 208,
      rect: true,
      color: getColor(grey920),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: [0, 0, BORDER_RADIUS, BORDER_RADIUS],
      },
      EventDataWrapper: {
        w: (w) => w - INNER_PADDING * 2,
        h: (h) => h,
        flex: {
          direction: 'column',
          padding: INNER_PADDING,
        },
        ...(data?.details?.text && {
          Details: {
            h: 36,
            flexItem: {
              marginBottom: INNER_MARGIN,
            },
            text: {
              text: data?.details?.text,
              textColor: getColor(grey300),
              fontFace: UniNeueRegular,
              fontSize: DETAILS_FONT_SIZE,
              maxLines: 1,
              maxLinesSuffix: '...',
              wordWrapWidth: cardWidth - INNER_PADDING * 2,
            },
          },
        }),
        Title: {
          h: 72,
          flexItem: {
            marginBottom: INNER_MARGIN,
          },
          text: {
            text: data?.title?.text ?? '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
            maxLines: 2,
            lineHeight: 36,
            maxLinesSuffix: '...',
            wordWrapWidth: cardWidth - INNER_PADDING * 2,
          },
        },
        Footer: {
          h: 36,
          text: {
            text: data?.footer?.text ?? '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: FOOTER_FONT_SIZE,
            maxLines: 1,
            maxLinesSuffix: '...',
            wordWrapWidth: cardWidth - INNER_PADDING * 2,
          },
        },
      },
      SportDataWrapper: {
        w: (w) => w - INNER_PADDING * 2,
        h: (h) => h,
        visible: false,
      },
    },
  }
}

export const getMediumTemplate = (cardWidth: number, data: CardItemModel) => {
  return {
    collision: true,
    Placeholder: {
      type: TvCardImgPlaceholder,
      imgSize: { w: 220, h: 200 },
    },
    Image: {
      w: (w) => w,
      h: LARGE_IMG_HEIGHT,
      src: data?.preview_image?.url,
      alpha: HIDDEN,
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BORDER_RADIUS,
      },
      Overlay: {
        x: -1,
        w: (w) => w + OVERLAY_WIDTH_ADJUSTMENT,
        h: (h) => h / 2 + OVERLAY_HEIGHT_ADJUSTMENT,
        mountY: -1,
        rect: true,
        colorBottom: getColor(black),
        colorTop: transparent,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: [0, 0, BORDER_RADIUS, BORDER_RADIUS],
        },
      },
    },
    InnerContainer: {
      w: (w) => w,
      h: (h) => h,
      flex: {
        direction: 'column',
      },
      TopBadges: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        TopLeadingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(data?.leading_top_badges?.length > 0 && {
            data: data.leading_top_badges[0],
          }),
          flexItem: {
            marginLeft: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
        TopTrailingBadges: {
          type: TvBadges,
          ...(data?.trailing_top_badges?.length > 0 && {
            badges: data.trailing_top_badges,
          }),
          flexItem: {
            marginRight: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
      },
      Metadata: {
        w: (w) => w,
        h: 72,
        flex: {
          direction: 'column',
          padding: INNER_PADDING,
        },
        Details: {
          h: 36,
          text: {
            text: data?.details?.text ?? '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: DETAILS_FONT_SIZE,
            maxLines: 1,
            maxLinesSuffix: '...',
            wordWrapWidth: cardWidth - INNER_MARGIN,
          },
        },
        Title: {
          h: 39,
          text: {
            text: data?.title?.text ?? '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
            maxLines: 1,
            maxLinesSuffix: '...',
            wordWrapWidth: cardWidth - INNER_MARGIN * 2,
          },
        },
      },
    },
  }
}

export const getSmallTemplate = (cardWidth: number, data: CardItemModel, isVideo = false) => {
  const { duration } = getVideoProgressAndDuration(data)

  return {
    flex: {
      direction: 'column',
    },
    collision: true,
    ImageWrapper: {
      w: (w) => w,
      h: SMALL_IMG_HEIGHT,
      rect: true,
      color: getColor(grey800),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: [BORDER_RADIUS, BORDER_RADIUS, 0, 0],
      },
      Placeholder: {
        type: TvCardImgPlaceholder,
        imgSize: { w: 220, h: 200 },
      },
      Image: {
        w: (w) => w,
        h: (h) => h,
        src: data?.preview_image?.url,
        alpha: HIDDEN,
      },
      TopBadges: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        TopLeadingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(data?.leading_top_badges?.length > 0 && {
            data: data.leading_top_badges[0],
          }),
          flexItem: {
            marginLeft: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
        TopTrailingBadges: {
          type: TvBadges,
          ...(data?.trailing_top_badges?.length > 0 && {
            badges: data.trailing_top_badges,
          }),
          flexItem: {
            marginRight: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
      },
      BottomBadges: {
        w: (w) => w,
        h: (h) => h,
        y: 158,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        BottomLeadingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(data?.leading_bottom_badges?.length > 0 && {
            data: data.leading_bottom_badges[0],
          }),
          flexItem: {
            marginLeft: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
        BottomTrailingBadge: {
          type: TvBadge,
          maxWidth: cardWidth - INNER_PADDING * 2,
          ...(isVideo &&
            duration && {
              duration,
            }),
          ...(data?.trailing_bottom_badges?.length > 0 && {
            badges: data.trailing_bottom_badges[0],
          }),
          flexItem: {
            marginRight: INNER_MARGIN,
            marginTop: INNER_MARGIN,
          },
        },
      },
    },
    MetadataContainer: {
      w: (w) => w - INNER_PADDING * 2,
      h: 185,
      rect: true,
      color: getColor(grey920),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: [0, 0, BORDER_RADIUS, BORDER_RADIUS],
      },
      flex: {
        direction: 'column',
        padding: INNER_PADDING,
      },
      ...(data?.details?.text && {
        Details: {
          h: 36,
          flexItem: {
            marginBottom: INNER_MARGIN / 2,
          },
          text: {
            text: data?.details?.text ?? '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: DETAILS_FONT_SIZE,
            maxLines: 1,
            maxLinesSuffix: '...',
            wordWrapWidth: cardWidth - INNER_PADDING * 2,
          },
        },
      }),
      Title: {
        h: 72,
        flexItem: {
          marginBottom: INNER_MARGIN / 2,
        },
        text: {
          text: data?.title?.text ?? '',
          textColor: getColor(grey50),
          fontFace: UniNeueBold,
          fontSize: TITLE_FONT_SIZE,
          maxLines: 2,
          maxLinesSuffix: '...',
          wordWrapWidth: cardWidth - INNER_PADDING * 2,
        },
      },
      Footer: {
        h: 36,
        text: {
          text: data?.footer?.text ?? '',
          textColor: getColor(grey300),
          fontFace: UniNeueRegular,
          fontSize: FOOTER_FONT_SIZE,
          maxLines: 1,
          maxLinesSuffix: '...',
          wordWrapWidth: cardWidth - INNER_PADDING * 2,
        },
      },
    },
  }
}
